import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosClient';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.ve-root {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  font-family: 'Outfit', sans-serif;
  padding: 24px;
}

.ve-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 18px;
  padding: 40px 36px;
  box-shadow: 0 20px 50px rgba(0,0,0,.06);
  text-align: center;
}

.ve-icon {
  width: 56px; height: 56px; border-radius: 50%;
  background: rgba(255,215,0,.12);
  border: 1.5px solid rgba(255,215,0,.3);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.3rem; color: #b8960c;
}

.ve-title { font-size: 1.5rem; font-weight: 900; color: #0d0d0d; margin-bottom: 8px; letter-spacing: -.02em; }
.ve-sub { font-size: .85rem; color: #64748b; margin-bottom: 28px; line-height: 1.6; font-weight: 500; }
.ve-sub strong { color: #0d0d0d; }

.ve-err {
  background: #fef2f2; border: 1.5px solid #fecaca; border-radius: 10px;
  padding: 11px 14px; margin-bottom: 18px; font-size: .8rem; color: #b91c1c;
  font-weight: 600; text-align: left; display: flex; gap: 8px; align-items: center;
}
.ve-info {
  background: #f0fdf4; border: 1.5px solid #bbf7d0; border-radius: 10px;
  padding: 11px 14px; margin-bottom: 18px; font-size: .8rem; color: #15803d;
  font-weight: 600; text-align: left; display: flex; gap: 8px; align-items: center;
}

.ve-code-row { display: flex; gap: 8px; justify-content: center; margin-bottom: 22px; }
.ve-code-input {
  width: 44px; height: 54px; text-align: center;
  font-size: 1.3rem; font-weight: 700; color: #0d0d0d;
  border: 2px solid #e2e8f0; border-radius: 10px; outline: none;
  font-family: 'Outfit', sans-serif;
  transition: border-color .15s, box-shadow .15s;
}
.ve-code-input:focus { border-color: #FFD700; box-shadow: 0 0 0 4px rgba(255,215,0,.12); }

.ve-btn {
  width: 100%; padding: 14px; font-size: .92rem; font-weight: 800;
  background: linear-gradient(135deg, #FFD700, #e6c200);
  color: #0d0d0d; border: none; border-radius: 12px; cursor: pointer;
  font-family: 'Outfit', sans-serif; transition: all .2s;
  box-shadow: 0 6px 22px rgba(255,215,0,.35);
  display: flex; align-items: center; justify-content: center; gap: 8px;
  margin-bottom: 16px;
}
.ve-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(255,215,0,.45); }
.ve-btn:disabled { opacity: .5; cursor: not-allowed; transform: none; }

@keyframes ve-spin { to { transform: rotate(360deg); } }
.ve-spin {
  display: inline-block; width: 14px; height: 14px;
  border: 2.5px solid rgba(0,0,0,.2); border-top-color: #0d0d0d;
  border-radius: 50%; animation: ve-spin .7s linear infinite;
}

.ve-resend { font-size: .8rem; color: #64748b; font-weight: 500; }
.ve-resend button {
  background: none; border: none; color: #0d0d0d; font-weight: 800;
  cursor: pointer; font-family: 'Outfit', sans-serif; font-size: .8rem;
  border-bottom: 2px solid #FFD700; padding: 0 0 1px;
}
.ve-resend button:disabled { color: #94a3b8; border-color: transparent; cursor: not-allowed; }
`;

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [email, setEmail] = useState('');
  const inputsRef = useRef([]);

  const sendOtp = async (isResend = false) => {
    setError('');
    setSending(true);
    try {
      await api.post('/email/send-otp/', {});
      setInfo(isResend ? 'A new code has been sent to your email.' : 'A verification code has been sent to your email.');
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setInfo('A code was already sent recently — check your inbox.');
        setCooldown(RESEND_COOLDOWN);
      } else if (status === 400) {
        // Backend says "already verified" — nothing left to do here.
        navigate('/dashboard');
      } else {
        setError(err.response?.data?.detail || 'Could not send verification code.');
      }
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    // Confirm we're actually authenticated (signup logs the user in
    // immediately) and find out who we're verifying / whether it's
    // already done.
    api.get('/me/')
      .then(res => {
        if (res.data.is_verified) {
          navigate('/dashboard');
          return;
        }
        setEmail(res.data.email || '');
        sendOtp(false);
      })
      .catch(() => {
        // Not authenticated — session expired, or this page was hit
        // directly without signing up/in first.
        navigate('/login');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleDigitChange = (i, value) => {
    const v = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < CODE_LENGTH - 1) inputsRef.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    setDigits(Array.from({ length: CODE_LENGTH }, (_, i) => pasted[i] || ''));
    inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length !== CODE_LENGTH) {
      setError(`Enter the ${CODE_LENGTH}-digit code.`);
      return;
    }
    setError('');
    setVerifying(true);
    try {
      await api.post('/email/verify-otp/', { code });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="ve-root">
        <div className="ve-card">
          <div className="ve-icon"><i className="fas fa-envelope-circle-check" /></div>
          <h2 className="ve-title">Verify your email</h2>
          <p className="ve-sub">
            Enter the {CODE_LENGTH}-digit code sent to{' '}
            <strong>{email || 'your email'}</strong>
          </p>

          {error && <div className="ve-err"><i className="fas fa-exclamation-circle" />{error}</div>}
          {info && !error && <div className="ve-info"><i className="fas fa-circle-check" />{info}</div>}

          <form onSubmit={handleVerify}>
            <div className="ve-code-row" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => (inputsRef.current[i] = el)}
                  className="ve-code-input"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={e => handleDigitChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  disabled={verifying}
                />
              ))}
            </div>

            <button type="submit" className="ve-btn" disabled={verifying}>
              {verifying ? <><span className="ve-spin" />Verifying…</> : <><i className="fas fa-check" />Verify email</>}
            </button>
          </form>

          <p className="ve-resend">
            Didn't get a code?{' '}
            <button onClick={() => sendOtp(true)} disabled={sending || cooldown > 0}>
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
          </p>
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
